/* eslint-disable @typescript-eslint/indent */
import { Injector, Pipe, PipeTransform } from "@angular/core";
import { map } from "rxjs/operators";
import { AnyObject } from "../../../types/app.type";
import { getMapOf } from "../../../utils/collections/map";
import { dotNotationToObject, mergeObjects } from "../../../utils/objects/object";
import { compare } from "../../../utils/values";
import { MAX_ROWS_TO_EXPORT } from "../../export-file/export-file.component";
import { DataTableService } from "../data-table.service";
import {
  DataTable,
  DataTableColumn,
  DataTableParams,
  DataTableParamsRelations,
  DataTableParamsSelect,
} from "../data-table.type";

const exportTypes = ["browser", "email"] as const;
type Output = typeof exportTypes[number];

const defaultInvalidValue = "";
const valuesMapping = {
  undefined: defaultInvalidValue,
  null: defaultInvalidValue,
  true: "Yes",
  false: "No",
};

@Pipe({ name: "exportData" })
export class ExportDataPipe implements PipeTransform {
  constructor(private injector: Injector, private dataTableService: DataTableService) {}

  transform(_: unknown, output: Output, dataTableConfig: DataTable<unknown>, getFilters: () => AnyObject): unknown {
    if (!exportTypes.includes(output)) {
      throw new Error("'ExportTo' param is not valid");
    }

    const exportCsv = dataTableConfig?.toolBar?.exportCsv;

    if (!exportCsv) {
      throw new Error("'exportCsv' object is not valid");
    }

    if (exportCsv.hidden) {
      return;
    }

    // Remove undesired properties
    const columns = dataTableConfig.columns.filter((col) => !col.exportCsv?.skipProperty);

    const queryParams: DataTableParams & { csvMapper?: Record<string, unknown> } = {
      where: getFilters(),
      pagination: this.getPagination(output),
      relations: this.getRelations(columns),
      select: this.getSelect(columns),
      order: dataTableConfig.sortValues,
      ...(output === "email" && { csvMapper: this.getCsvMapper(columns) }),
    };

    if (output === "browser") {
      const columnsMapping = getMapOf(columns, "prop");

      return exportCsv.data
        .browser?.(queryParams)
        .pipe(map((data) => data.items.map((item) => this.mapItem(item, columnsMapping))));
    }

    if (output === "email") {
      return exportCsv.data.email?.(queryParams);
    }
  }

  /** Map the data to be displayed on the csv file */
  private mapItem(item: unknown, columnsMapping: Map<unknown, DataTableColumn<unknown>>): AnyObject<unknown> {
    const columnKeys = Array.from(columnsMapping.values()).map((col) => col.prop.toString());

    const getValue = (key: string) =>
      item[key] ??
      key?.split(".").reduce((a, b) => a?.[b], item) ??
      [key?.split(".")[0]].reduce((a, b) => a?.[b], item);

    const result = columnKeys.reduce((prev, curr) => {
      const column = columnsMapping.get(curr);
      const columnName = column.name;
      const columnKeyOrKeys = column.exportCsv?.browserColumnKey || curr;

      let value: unknown;

      if (Array.isArray(columnKeyOrKeys)) {
        columnKeyOrKeys.forEach((key) => {
          if (value === undefined) {
            value = getValue(key);
          }
        });
      } else {
        const key = columnKeyOrKeys;
        value = getValue(key);
      }

      value =
        value !== undefined
          ? this.dataTableService.parseData(item, column, value, false)
          : valuesMapping[undefined as string];

      return {
        ...prev,
        ...{ [columnName]: value },
      };
    }, {});

    return result;
  }

  /** Get the pagination param for the query params */
  private getPagination(output: Output): { page: number; pageSize: number } {
    return output === "browser" ? { page: 1, pageSize: MAX_ROWS_TO_EXPORT } : undefined;
  }

  /** Get the relation param for the query params */
  private getRelations(columns: DataTableColumn<unknown>[]): DataTableParamsRelations<unknown> {
    const relationsAsDotNotation = Array.from(
      new Set(
        columns
          .filter((col) => this.isRelation(col.prop.toString()))
          .map((col) => col.prop.toString().split(".").slice(0, -1).join(".")),
      ),
    ).sort((a, b) => compare(a, b));

    const relations = relationsAsDotNotation.map((key) => dotNotationToObject(key));

    return mergeObjects(relations);
  }

  /** Get the select param for the query params */
  private getSelect(columns: DataTableColumn<unknown>[]): DataTableParamsSelect<unknown> {
    return columns?.reduce<DataTableParamsSelect<unknown>>(
      (acc, col) => mergeObjects([acc, this.createSelectObject(col.prop.toString())]),
      {},
    );
  }

  /** Get the csvMapper param for the query params */
  private getCsvMapper(columns: DataTableColumn<unknown>[]): AnyObject<string> {
    const keyName = [];

    columns.forEach((col) => {
      const keyOrKeys = col.exportCsv?.csvMapperKey || col.prop;
      const name = col.name;
      if (Array.isArray(keyOrKeys)) {
        keyOrKeys.forEach((key) => keyName.push({ key, name }));
      } else {
        keyName.push({ key: keyOrKeys, name });
      }
    });

    const result = keyName.reduce((prev, curr) => {
      const { key, name } = curr;

      return {
        ...prev,
        [key]: name,
      };
    }, {});

    return result;
  }

  /** Create the select object used for the select param */
  private createSelectObject(key: string, obj = {}): AnyObject {
    const isRelation = this.isRelation(key);

    if (isRelation) {
      const keys = key.split(".");
      const keysString = keys.slice(0, -1).join(".");
      const value = Object.values(obj)[0];
      if (Object.keys(obj).length) {
        /** @workaround We have to include the "id" (PK) when "select" is a object and we have inner relation */
        const id = { id: true };
        return this.createSelectObject(keysString, { [keysString]: { ...id, [keys.slice(-1)[0]]: value } });
      }
      return this.createSelectObject(keysString, { [keysString]: { [keys.slice(-1)[0]]: true } });
    } else if (Object.keys(obj).length) {
      return obj;
    }
    return { [key]: true };
  }

  /** Check if column is a relationship */
  private isRelation(columnKey: string): boolean {
    return columnKey.toString().indexOf(".") > -1;
  }
}
